import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { PawPrint, Camera, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';


const getPhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return path.startsWith('/api')
    ? `${process.env.REACT_APP_BACKEND_URL}${path}`
    : `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
};

const PET_SIZES = [
  { id: 'pequeno', label: 'Pequeño' },
  { id: 'mediano', label: 'Mediano' },
  { id: 'grande', label: 'Grande' },
];

const PET_SEX = [
  { id: 'macho', label: 'Macho' },
  { id: 'hembra', label: 'Hembra' },
];

export const PetForm = ({ pet = null, onSaved, onCancel }) => {
  const isEditing = !!pet?.pet_id;
  const [form, setForm] = useState({
    name: '',
    breed: '',
    size: 'mediano',
    sex: 'macho',
    age: '',
    photo: null
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (pet) {
      setForm({
        name: pet.name || '',
        breed: pet.breed || '',
        size: pet.size || 'mediano',
        sex: pet.sex || 'macho',
        age: pet.age || '',
        photo: pet.photo || null
      });
      setPreview(null);
    }
  }, [pet]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/pets/upload-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, photo: res.data.url }));
    } catch {
      toast.error('Error al subir foto');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Ingresa el nombre de tu mascota');
      return;
    }

    if (!form.breed.trim()) {
      toast.error('Ingresa la raza de tu mascota');
      return;
    }

    if (!form.size) {
      toast.error('Selecciona el tamaño de tu mascota');
      return;
    }

    if (!form.sex) {
      toast.error('Selecciona el sexo de tu mascota');
      return;
    }

    if (!form.age || parseInt(form.age) <= 0) {
      toast.error('Ingresa una edad válida');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        species: 'perro'
      };

      const res = isEditing
        ? await api.put(`/pets/${pet.pet_id}`, payload)
        : await api.post('/pets', payload);

      toast.success(isEditing ? 'Mascota actualizada' : 'Mascota agregada');
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 space-y-5" data-testid="pet-form">
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="relative w-28 h-28 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 hover:border-[#E6202E] flex items-center justify-center overflow-hidden transition-colors"
          data-testid="pet-photo-upload"
        >
          {preview || form.photo ? (
            <img
              src={preview || getPhotoUrl(form.photo)}
              alt="Mascota"
              className="w-full h-full object-cover"
            />
          ) : (
            <Camera className="w-8 h-8 text-gray-400" />
          )}

          {uploading && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white border-t-transparent animate-spin rounded-full" />
            </div>
          )}
        </button>

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
      </div>

      <p className="text-center text-xs text-gray-500">Foto opcional</p>

      <div>
        <label className="block text-sm font-medium mb-1">Nombre *</label>
        <Input placeholder="Ej: Max" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Raza *</label>
        <Input placeholder="Ej: Labrador, Mestizo" value={form.breed} onChange={e => setForm({ ...form, breed: e.target.value })} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Tamaño *</label>
          <div className="flex gap-2">
            {PET_SIZES.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setForm({ ...form, size: s.id })}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                  form.size === s.id ? 'bg-[#E6202E] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Sexo *</label>
          <div className="flex gap-2">
            {PET_SEX.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setForm({ ...form, sex: s.id })}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                  form.sex === s.id ? 'bg-[#E6202E] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Edad (años) *</label>
        <Input type="number" placeholder="Ej: 3" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} />
      </div>

      <div className="flex gap-3">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancelar
          </Button>
        )}

        <Button onClick={handleSave} disabled={saving || uploading} className="flex-1 bg-[#E6202E] hover:bg-[#D31522]">
          {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Guardar mascota'}
        </Button>
      </div>
    </div>
  );
};

export default function PetSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = params.get('returnTo') || localStorage.getItem('ucan_after_pet_redirect');
  const [pets, setPets] = useState([]);
  const [showForm, setShowForm] = useState(true);

  const loadPets = async () => {
    try {
      const res = await api.get('/pets');
      setPets(res.data);
    } catch {}
  };

  const handlePetSaved = () => {
    loadPets();
    setShowForm(false);
  };

  const handleContinue = () => {
    if (pets.length === 0) {
      toast.error('Primero agrega tu mascota para crear una solicitud');
      return;
    }

    navigate('/client/requests?onboarding=1');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="pet-setup-page">
      <div className="max-w-lg mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <PawPrint className="w-8 h-8 text-[#E6202E]" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Agrega tu mascota</h1>
          <p className="text-gray-500 mt-2">Así los cuidadores podrán conocer mejor a tu compañerito</p>
        </div>

        {pets.length > 0 && (
          <div className="space-y-3 mb-6">
            {pets.map(pet => (
              <div key={pet.pet_id} className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                {pet.photo ? (
                  <img src={getPhotoUrl(pet.photo)} alt={pet.name} className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                    <PawPrint className="w-6 h-6 text-[#E6202E]" />
                  </div>
                )}

                <div className="flex-1">
                  <h3 className="font-bold">{pet.name}</h3>
                  <p className="text-sm text-gray-500">{pet.breed || 'Sin raza'} • {pet.size} • {pet.sex}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm ? (
          <PetForm onSaved={handlePetSaved} onCancel={pets.length > 0 ? () => setShowForm(false) : null} />
        ) : (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-white rounded-2xl shadow-sm p-5 border-2 border-dashed border-gray-300 hover:border-[#E6202E] flex items-center justify-center gap-3 text-gray-500 hover:text-[#E6202E] transition-colors"
          >
            <Plus className="w-5 h-5" /> Agregar otra mascota
          </button>
        )}

        <div className="mt-8 space-y-3">
          <Button onClick={handleContinue} className="w-full bg-[#E6202E] hover:bg-[#D31522] py-6 text-lg" disabled={pets.length === 0}>
            Continuar para crear solicitud <ChevronRight className="w-5 h-5 ml-2" />
          </Button>

          {pets.length === 0 && (
            <p className="text-center text-xs text-gray-400">Podrás agregar tus mascotas más tarde desde tu perfil</p>
          )}
        </div>
      </div>
    </div>
  );
}
